import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { PureComponent } from "react";

import { DesignToken, Loader, SpacingToken } from "@livechat/design-system-react-components";

import { IFilterUpdate } from "components/filters/interfaces";
import Flex from "components/layout/Flex";
import { Filter, IFilters } from "constants/filters";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { TicketsBatchAction } from "constants/tickets/batch-action";
import { routeSupportsBatchUpdate } from "helpers/tickets";
import { ITicket } from "interfaces/entities/ticket";
import { IAgent } from "store/entities/agents";
import { ITag } from "store/entities/tags";
import { ITeam } from "store/entities/teams";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import { TicketsEmptyState } from "./TicketsEmptyState";
import { TicketsFilters } from "./TicketsFilters";
import { TicketsListTable } from "./TicketsListTable";
import { TicketsPagination } from "./TicketsPagination";
import TicketsSilos from "./TicketsSilos";

interface IProps {
  canCreateTickets: boolean;
  data: ITicket[];
  dataFetchedForRoute: TicketsRoutes | null;
  currentAgent: IAgent;
  currentPage: number;
  totalPages: number;
  totalResults: number;
  route: TicketsRoutes;
  anyDataFetched: boolean;
  agents: IAgent[];
  ticketsFromOtherTeamsAllowed: boolean;
  teams: ITeam[];
  allTeams: ITeam[];
  teamsUsageAllowed: boolean;
  areMailboxesSetUp: boolean;
  canAccessSettings: boolean;
  tags: ITag[];
  allTags: ITag[];
  filters?: IFilters;
  filtersOrder?: Filter[];
  searchQuery?: string;
  selectedIds?: string[];
  isBatchUpdating: boolean;
  isFetching: boolean;
  onFilterUpdate(filterUpdate: IFilterUpdate): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBatchUpdate(ticketIds: string[], action: TicketsBatchAction, payload?: any): void;
  onSelectedIdsUpdate(ticketIds: string[]): void;
  onPageChange(page: number): void;
  onSearchClear(): void;
  onAllTicketsLinkClick(route: TicketsRoutes): void;
  onSpamModalRequest(type: "spam" | "unspam", selectedIds: string[]): void;
}

export class TicketsList extends PureComponent<IProps> {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleAllTicketsLinkClick = () => {
    this.props.onAllTicketsLinkClick(this.props.route);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render() {
    const {
      agents,
      currentAgent,
      canCreateTickets,
      data,
      dataFetchedForRoute,
      currentPage,
      ticketsFromOtherTeamsAllowed,
      totalPages,
      totalResults,
      teams,
      teamsUsageAllowed,
      tags,
      filters,
      route,
      anyDataFetched,
      areMailboxesSetUp,
      canAccessSettings,
      filtersOrder,
      isBatchUpdating,
      selectedIds,
      onBatchUpdate,
      onSelectedIdsUpdate,
      searchQuery,
      onSearchClear,
      onPageChange,
      onFilterUpdate,
      allTeams,
      onSpamModalRequest,
      allTags,
      isFetching,
    } = this.props;

    const isDataFresh = dataFetchedForRoute === route;

    return (
      <Wrapper>
        <FiltersWrapper>
          <TicketsFilters
            currentAgent={currentAgent}
            route={route}
            filters={filters as IFilters}
            filtersOrder={filtersOrder as Filter[]}
            agents={agents}
            teams={teams}
            allTeams={allTeams}
            teamsUsageAllowed={teamsUsageAllowed}
            tags={tags}
            allTags={allTags}
            onFilterUpdate={onFilterUpdate}
          />
        </FiltersWrapper>
        {searchQuery && (
          <TicketsSilos
            filters={filters as IFilters}
            searchQuery={searchQuery}
            data={data}
            anyDataFetched={anyDataFetched}
          />
        )}
        {totalResults > 0 && (
          <TicketsPagination
            route={route}
            ticketsFromOtherTeamsAllowed={ticketsFromOtherTeamsAllowed}
            isDataFresh={isDataFresh}
            currentPage={currentPage}
            totalPages={totalPages}
            supportsBatchUpdate={routeSupportsBatchUpdate(route, filters as IFilters, currentPage)}
            totalResults={totalResults}
            onPageChange={onPageChange}
            selectedIds={selectedIds as string[]}
            onAllTicketsLinkClick={this.handleAllTicketsLinkClick}
          />
        )}
        {isFetching && (
          <Flex data-testid="ticket-list-loader" css={loaderOverlayStyles}>
            <Loader />
          </Flex>
        )}
        <TicketsListTable
          currentPage={currentPage}
          currentAgent={currentAgent}
          agents={agents}
          filters={filters as IFilters}
          canCreateTickets={canCreateTickets}
          data={data}
          teams={teams}
          isBatchUpdating={isBatchUpdating}
          route={route}
          isDataFresh={isDataFresh}
          selectedIds={selectedIds}
          onBatchUpdate={onBatchUpdate}
          onSpamModalRequest={onSpamModalRequest}
          onSelectedIdsUpdate={onSelectedIdsUpdate}
        />
        {anyDataFetched && data.length === 0 && (
          <TicketsEmptyState
            route={route}
            isDataFresh={isDataFresh}
            filters={filters as IFilters}
            searchQuery={searchQuery as string}
            areMailboxesSetUp={areMailboxesSetUp}
            canAccessSettings={canAccessSettings}
            onSearchClear={onSearchClear}
          />
        )}
      </Wrapper>
    );
  }
}

const Wrapper = styled.div`
  height: 100%;
  overflow: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
`;

const FiltersWrapper = styled.div`
  padding: var(${SpacingToken.Spacing2}) var(${SpacingToken.Spacing3});
  border-bottom: 1px solid var(${DesignToken.BorderBasicSecondary});
`;

const loaderOverlayStyles = css`
  display: flex;
  position: absolute;
  height: 100%;
  z-index: ${StackingContextLevel.ContentOverlay};
  width: 100%;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
    opacity: 0.6;
  }
`;
